import { Box, BoxProps, Flex, Text } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

import RouteLink from '@/components/RouteLink';
import { RegistryComposition, RegistryPackage } from '@/graphql/upbound-graphql';
import { isRegistryComposition } from '@/utils/guards/models';
import useParams from '@/utils/hooks/useParams';

import PackageTabContent from './PackageTabContent';

const [firstColumnWidth, secondColumnWidth, thirdColumnWidth] = ['420px', '505px', '100px'];

const headers = [
  {
    key: 'compositions-name-header',
    children: 'Name',
    width: firstColumnWidth,
  },
  {
    key: 'compositions-referenced-xrd-header',
    children: 'Referenced XRD',
    width: secondColumnWidth,
  },
  {
    key: 'compositions-resources-header',
    children: 'Resources',
    width: thirdColumnWidth,
  },
];

const getExamples = (data: RegistryPackage) => {
  if (!data.compositions || data.compositions.length < 1) {
    return '';
  }

  const [firstComposition] = data.compositions;

  return firstComposition.name.split('.').slice(0, 2).join(', ');
};

const CompositionsTabContent: React.FC<
  BoxProps & {
    data: RegistryPackage;
  }
> = ({ data, ...props }) => {
  const router = useRouter();
  const params = useParams(router);
  const query = decodeURIComponent(params.get('query') || '');

  const prepend = `/${data.packageType.toLowerCase()}s/${data.account}/${data.name}/${data.version}`;

  const comps = (data.compositions || []).reduce<Array<RegistryComposition>>((result, composition) => {
    if (!isRegistryComposition(composition)) {
      return result;
    }

    if (!query) {
      return [...result, composition];
    }

    const sanitizedValue = query.toLowerCase().trim();

    if (
      !composition.name.toLowerCase().includes(sanitizedValue) &&
      !composition.xrdGroup?.toLowerCase().includes(sanitizedValue) &&
      !composition.xrdKind?.toLowerCase().includes(sanitizedValue)
    ) {
      return result;
    }

    return [...result, composition];
  }, []);

  const rows = useMemo(() => {
    if (comps.length < 1) {
      return (
        <Box m="30px 0">
          <Text textAlign="center" fontStyle="oblique">
            No Compositions found.
          </Text>
        </Box>
      );
    }

    return comps.map(row => (
      <Box
        key={`compositions-row-${row.name}/${row.xrdGroup}/${row.xrdKind}`}
        p={{ base: '30px 0', xl: '13px 0 15px 0' }}
        borderBottom="1px solid"
        borderBottomColor="white.4"
        width="100%"
      >
        <Flex display={{ base: 'flex', xl: 'none' }} flexDirection="column" alignItems="flex-start">
          <RouteLink
            prepend={prepend}
            to="compositionDetails"
            params={{
              compositionName: row.name,
              compositionXrdGroup: row.xrdGroup,
              compositionXrdKind: row.xrdKind,
            }}
            width="100%"
            fontSize="14px"
            fontWeight={900}
            textDecoration="underline"
            _hover={{ textDecoration: 'none' }}
            color="grey.0"
            mb="10px"
            noOfLines={1}
          >
            {row.name}
          </RouteLink>
          <Text as="span" fontSize="14px" lineHeight="20px" color="grey.0" mb="10px" noOfLines={1}>
            {row.xrdGroup}
          </Text>
          <Text as="span" fontSize="14px" color="grey.0">
            {row.resourceCount}
          </Text>
        </Flex>
        <Flex display={{ base: 'none', xl: 'flex' }}>
          <Box width={firstColumnWidth} p="15px" mr="auto">
            <RouteLink
              prepend={prepend}
              to="compositionDetails"
              params={{
                compositionName: row.name,
                compositionXrdGroup: row.xrdGroup,
                compositionXrdKind: row.xrdKind,
              }}
              fontSize="14px"
              fontWeight={900}
              textDecoration="underline"
              _hover={{ textDecoration: 'none' }}
              color="grey.0"
              noOfLines={1}
            >
              {row.name}
            </RouteLink>
          </Box>
          <Box width={secondColumnWidth} p="15px" mr="auto">
            <Text
              as="span"
              display="block"
              fontSize="14px"
              lineHeight="20px"
              color="grey.0"
              textDecoration="underline"
              mb="8px"
              noOfLines={1}
            >
              {row.xrdKind}
            </Text>
            <Text as="span" display="block" fontSize="14px" lineHeight="20px" color="grey.0" noOfLines={1}>
              {row.xrdGroup}
            </Text>
          </Box>
          <Box width={thirdColumnWidth} p="15px">
            <Text as="span" fontSize="14px" color="grey.0">
              {row.resourceCount}
            </Text>
          </Box>
        </Flex>
      </Box>
    ));
  }, [prepend, comps]);

  return (
    <PackageTabContent
      {...props}
      mt="35px"
      description="Compositions compose resources into higher-level APIs defined by a referenced Composite Resource Definition (XRD)."
      placeholder={`Search for Compositions (e.g. ${getExamples(data)})`}
      headers={headers}
      rows={rows}
      prepend={`${prepend}/compositions`}
    />
  );
};

export default CompositionsTabContent;
